/**
 * Lookup history for one domain.
 *
 * The summary row exists because the question people actually arrive with is
 * "is this being throttled", and counting rows by eye does not answer it. A
 * registry refusing us answers 429 or 403; those land here as ordinary rows, so
 * without the count the only visible symptom is that the domain is confirmed
 * less often than the schedule says — which looks like a scheduling bug rather
 * than a rate limit.
 */
import * as React from 'react'
import { useDomainObservations } from '@/lib/queries'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { formatDateTimeSeconds, formatNumber } from '@/lib/utils'

const OUTCOME: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  ok: { label: '成功', variant: 'success' },
  throttled: { label: '被限流', variant: 'warning' },
  failed: { label: '失败', variant: 'danger' },
}

const FILTERS = [
  { value: '', label: '全部' },
  { value: 'ok', label: '仅成功' },
  { value: 'throttled', label: '仅被限流' },
  { value: 'failed', label: '仅失败' },
]

export function ObservationHistory({
  domainId,
  domain,
  onClose,
}: {
  domainId: string | null
  domain: string
  onClose: () => void
}) {
  const [outcome, setOutcome] = React.useState('')
  const [source, setSource] = React.useState('')
  const {
    data: pages, isLoading, error, refetch,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useDomainObservations(domainId, {
    outcome: outcome || undefined,
    source: source || undefined,
    limit: 100,
  })

  const data = pages?.pages[0]
  const items = pages?.pages.flatMap((p) => p.items) ?? []

  return (
    <Dialog open={!!domainId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{domain} — 查询记录</DialogTitle>
        </DialogHeader>

        {error && <ErrorState error={error} onRetry={() => void refetch()} />}
        {isLoading && <LoadingState rows={5} />}

        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Stat label="总次数" value={formatNumber(data.summary.total)} />
              <Stat label="成功" value={formatNumber(data.summary.ok)} />
              <Stat
                label="被限流" value={formatNumber(data.summary.throttled)}
                danger={data.summary.throttled > 0}
              />
              <Stat
                label="失败" value={formatNumber(data.summary.failed)}
                danger={data.summary.failed > 0}
              />
              <Stat
                label="实测间隔"
                value={
                  data.summary.median_interval_seconds
                    ? `${Math.round(data.summary.median_interval_seconds)} 秒`
                    : '—'
                }
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setOutcome(f.value)}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                    outcome === f.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <span className="mx-1 text-muted-foreground">|</span>
              {[
                { value: '', label: '全部来源' },
                { value: 'rdap', label: 'RDAP' },
                { value: 'whois', label: 'WHOIS' },
              ].map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSource(s.value)}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                    source === s.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="max-h-[24rem] overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b bg-background text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">时间</th>
                    <th className="px-3 py-2 font-medium">来源</th>
                    <th className="px-3 py-2 font-medium">结果</th>
                    <th className="px-3 py-2 text-right font-medium">HTTP</th>
                    <th className="px-3 py-2 text-right font-medium">耗时</th>
                    <th className="px-3 py-2 font-medium">返回状态</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                        没有符合条件的记录
                      </td>
                    </tr>
                  )}
                  {items.map((o) => {
                    const v = OUTCOME[o.outcome] ?? OUTCOME.failed
                    return (
                      <tr key={o.id} className="border-b last:border-0">
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                          {formatDateTimeSeconds(o.queried_at)}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{o.source}</td>
                        <td className="px-3 py-2"><Badge variant={v.variant}>{v.label}</Badge></td>
                        <td className={`px-3 py-2 text-right tabular-nums ${
                          o.outcome === 'throttled' ? 'font-semibold text-amber-700 dark:text-amber-400' : ''
                        }`}>
                          {o.http_status || '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {o.latency_ms ? `${o.latency_ms}ms` : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {o.error_message || o.raw_statuses?.join(', ') || o.normalized_status}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Without this the dialog showed one page and said nothing, while
                the total above it counted the whole history — the two numbers
                disagreed and only one of them was labelled. */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                已显示 {formatNumber(items.length)} 条
                {!outcome && !source && <> / 共 {formatNumber(data.summary.total)} 条</>}
              </span>
              {hasNextPage && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? '加载中…' : '加载更多'}
                </Button>
              )}
            </div>

            {data.summary.throttled > 0 && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-400">
                出现过 {data.summary.throttled} 次被限流（HTTP 429 或 403）。注册局在拒绝我们，
                说明该主机的 qps 配得偏高——到「系统运维 → 限速校准」下调，并注意实测间隔是否已被拉长。
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Stat({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${danger ? 'text-destructive' : ''}`}>
        {value}
      </div>
    </div>
  )
}
